/**
 * Level <-> spell-criteria pruning, shared between the Generator page's build
 * panel (level control, src/hooks/useBuildPanelControls.js — cluster D) and
 * the criteria sheet (cluster B, see docs/generator-slicing.md). Both call
 * these when the character level changes, so they must stay one
 * implementation rather than diverging per-hook copies.
 *
 * Split out of src/core/spellScoreTerms.js (rather than added there) to keep
 * that file under the repo's 400-line eslint cap — pure, no React, no
 * component state.
 */

import { calcSpellRows, isEliotropeClass, isSpellCriterionAvailableAtLevel } from './spellUtils';
import Build from './Build';

export function canPruneSpellCriteriaForCurrentClass(build, spellTypeId, spellScoringData) {
    if (!build || typeof build.getMaximizeSpellCriteria !== 'function') return false;
    const criteria = build.getMaximizeSpellCriteria();
    if (!Array.isArray(criteria) || criteria.length === 0) return false;
    if (!Number.isFinite(Number(spellTypeId))) return false;
    return Number(spellScoringData?.loadedTypeId) === Number(spellTypeId);
}

export function pruneLevelUnavailableSpellCriteria(build, spellScoringData, lvl) {
    if (!build || typeof build.getMaximizeSpellCriteria !== 'function') return build;
    const currentCriteria = build.getMaximizeSpellCriteria();
    if (!Array.isArray(currentCriteria) || currentCriteria.length === 0) return build;

    const spellList = [
        ...(Array.isArray(spellScoringData?.spells) ? spellScoringData.spells : []),
        ...(Array.isArray(spellScoringData?.other) ? spellScoringData.other : []),
    ];
    if (!spellList.length) return build;

    const spellById = new Map(
        spellList
            .map((sp) => [Number(sp?.id), sp])
            .filter(([id]) => Number.isFinite(id))
    );
    const stats = build?.getStats?.({ includePassiveEffects: true }) || null;
    const clsName = build?.getCharacterClass?.()?.name || build?.characterClass?.name;
    const eliotropePortalCardOnly = isEliotropeClass(clsName);
    const rows = calcSpellRows({
        spells: spellList,
        lvl,
        stats,
        lang: 'fr',
        eliotropePortalCardOnly,
        summonCatalog: spellScoringData?.summonCatalog || {},
        charges: build?.spellCharges,
    });
    const rowById = new Map(
        rows
            .map((row) => [Number(row?.id), row])
            .filter(([id]) => Number.isFinite(id))
    );
    const kept = currentCriteria.filter((criterion) => (
        isSpellCriterionAvailableAtLevel({ criterion, spellById, lvl, rowById })
    ));
    if (kept.length === currentCriteria.length) return build;

    const keptSpellIds = [];
    const keptSpellCriteria = [];
    kept.forEach((criterion) => {
        if (typeof criterion === 'number') {
            const id = Number(criterion);
            if (Number.isFinite(id)) keptSpellIds.push(id);
            return;
        }
        if (criterion && typeof criterion === 'object') {
            keptSpellCriteria.push(criterion);
        }
    });

    return new Build({
        ...build,
        maximizeSpellIds: keptSpellIds,
        maximizeSpellCriteria: keptSpellCriteria,
    });
}
